<template>
  <div class="form-group" :class="$attrs.class" role="group">
    <label v-if="label" class="form-label" :for="id">
      {{ label }}
      <span v-if="required" class="required-asterisk">*</span>
      <input
        :id="id"
        ref="inputRef"
        v-model="model"
        :type="type"
        class="form-input"
        :disabled="disabled"
        :aria-label="label"
        :aria-required="required"
        :aria-invalid="!!error"
        :aria-describedby="error ? `${id}-error` : undefined"
        :autocomplete="autocomplete"
        :data-test-id="dataTestId"
        :placeholder="placeholder"
        @blur="handleBlur"
        @focus="handleFocus"
        @change="handleChange"
      />
    </label>

    <span
      v-show="error || $slots.error"
      :id="`${id}-error`"
      class="form-error"
      role="alert"
    >
      <slot name="error">
        {{ error }}
      </slot>
    </span>
  </div>
</template>

<script setup lang="ts">
import { useUniqueId } from '@/composables/useUniqueId'

withDefaults(
  defineProps<{
    label?: string
    type?: string
    disabled?: boolean
    required?: boolean
    error?: string
    id?: string
    autocomplete?: string
    dataTestId?: string
    placeholder?: string
  }>(),
  {
    id: () => useUniqueId('input').value,
    label: undefined,
    type: 'text',
    disabled: false,
    required: false,
    error: undefined,
    autocomplete: undefined,
    dataTestId: undefined,
    placeholder: undefined,
  }
)

const emit = defineEmits<{
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
  change: [event: Event]
  'update:model-value': [value: string]
}>()

const model = defineModel<string>()
const inputRef = ref<HTMLInputElement | null>(null)

const focusInput = () => {
  if (inputRef.value) {
    inputRef.value.focus()
  }
}

const handleBlur = (event: FocusEvent) => {
  emit('blur', event)
}

const handleFocus = (event: FocusEvent) => {
  emit('focus', event)
}

const handleChange = (event: Event) => {
  emit('change', event)
}

defineExpose({
  focusInput,
})
</script>

<style lang="postcss" scoped>
.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-light text-gray-700;
}

.form-input {
  @apply w-full rounded-lg px-2 h-[40px] border border-gray-300 mt-2;
  @apply focus:outline-primary hover:border-gray-400;
  @apply transition-all duration-200 ease-in-out;
  font-size: 16px;
  &:focus {
    box-shadow:
      0px 1px 1px rgba(0, 0, 0, 0.03),
      0px 3px 6px rgba(0, 0, 0, 0.02);
  }
}

.form-error {
  @apply text-sm text-red inline-block;
}

.required-asterisk {
  @apply text-red ml-0.5;
}
</style>
